import { Conversation, ConversationHistoryResponse } from '@/interface/chat';
import { formatDate, groupChatsByDate } from '@/util/date';
import { isDemoEnvironment } from '@/util/demoUtils';
import { isStaffConversationId } from '@/util/staffUtils';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from './useApi';
import { useProfile } from './useProfile';
import { useQueryClient } from '@tanstack/react-query';

function useConversationHistory() {
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const { i18n } = useTranslation(['navMenu']);
  const [showMore, setShowMore] = useState(false);
  const [localNewConversations, setLocalNewConversations] = useState(
    [] as Conversation[],
  );

  const isDemo = isDemoEnvironment();

  const {
    data: conversationsHistory,
    refetch: refetchConversations,
    isLoading,
  } = useApiQuery<ConversationHistoryResponse>(
    ['/user/getAllConversation', profile?.value[0].eXpMember.Uuid as string],
    '/user/getAllConversation',
    {
      staleTime: Infinity,
      enabled: !isDemo && !!profile?.value[0].eXpMember.Uuid,
    },
    false,
    {
      userId: profile?.value[0].eXpMember.Uuid as string,
      pageSize: 10, //pagesize to be changed as backend sends updated pagination
      // pageNumber: 1,
      cursor: '',
    },
  );
  const currentLocale = i18n.language || 'en-US'; // Get language from i18n

  const conversations = useMemo(() => {
    const serverItems = isDemo
      ? ([] as Conversation[])
      : conversationsHistory?.data.items || [];
    const merged = [...localNewConversations, ...serverItems];

    // To avoid same chat showing twice if it's already fetched
    const seen = new Set();
    return merged
      .filter((chat) => !isStaffConversationId(chat.uId)) // Exclude staff-created chats from sidebar
      .filter((chat) => {
        if (seen.has(chat.uId)) return false;
        seen.add(chat.uId);
        return true;
      });
  }, [localNewConversations, conversationsHistory, isDemo]);
  const addOrUpdateConversation = (conversation: Conversation) => {
    setLocalNewConversations((prev) => {
      const updated = prev.filter((c) => c.uId !== conversation.uId);
      const newList = [conversation, ...updated];

      // sorting only latest 10 conversations
      return newList
        .sort(
          (a, b) =>
            new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime(),
        )
        .slice(0, 10);
    });

    // Don't pollute the React Query cache with staff-created conversations
    if (isStaffConversationId(conversation.uId)) return;

    // Also update the shared React Query cache so all hook consumers
    const userId = profile?.value?.[0]?.eXpMember?.Uuid as string | undefined;

    if (!userId) return;

    queryClient.setQueryData<ConversationHistoryResponse>(
      ['/user/getAllConversation', userId],
      (oldData) => {
        if (!oldData) {
          return {
            data: {
              items: [conversation],
              nextCursor: '',
            },
          };
        }

        const existingItems = oldData.data.items || [];
        const filtered = existingItems.filter(
          (c) => c.uId !== conversation.uId,
        );

        return {
          ...oldData,
          data: {
            ...oldData.data,
            items: [conversation, ...filtered],
          },
        };
      },
    );
  };

  const sortedChats = useMemo(() => {
    const serverItems = isDemo
      ? ([] as Conversation[])
      : conversationsHistory?.data.items || [];
    return [...localNewConversations, ...serverItems]
      .filter((v, i, a) => a.findIndex((t) => t.uId === v.uId) === i) // Remove duplicates
      .filter((chat) => !isStaffConversationId(chat.uId)) // Exclude staff-created chats from sidebar
      .sort(
        (a, b) =>
          new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime(),
      )
      .slice(0, 10);
  }, [localNewConversations, conversationsHistory, isDemo]);

  const groupedChats = useMemo(
    () => groupChatsByDate(sortedChats, currentLocale),
    [sortedChats, currentLocale],
  );

  const chatDates = useMemo(
    () =>
      Object.keys(groupedChats).sort(
        (a, b) => new Date(b).getTime() - new Date(a).getTime(),
      ),
    [groupedChats],
  );

  const recentDates = chatDates.slice(0, 10);
  const visibleChats = showMore ? recentDates : recentDates.slice(0, 3);

  const todayConversation = useMemo(() => {
    return groupedChats[formatDate(new Date().toISOString(), currentLocale)];
  }, [groupedChats, currentLocale]);

  return {
    visibleChats,
    groupedChats,
    sortedChats,
    currentLocale,
    showMore,
    setShowMore,
    conversations,
    todayConversation,
    addOrUpdateConversation,
    refetchConversations,
    isLoading,
  };
}

export default useConversationHistory;
